/**
 * Character Status Utility
 *
 * Shared logic for determining character status based on HP and conditions.
 * Follows D&D 5E rules:
 * - PCs at 0 HP are "Unconscious" (making death saves)
 * - Monsters/NPCs at 0 HP are "Dead"
 * - Massive damage (negative HP >= max HP) = instant death
 */

import type { WireConditionEntry } from '@danda/shared';

export interface CharacterStatus {
  label: string;
  bgColor: string;
  textColor: string;
}

/** Extract condition name from WireConditionEntry (string or object with name) */
function getConditionName(condition: WireConditionEntry): string {
  return typeof condition === 'string' ? condition : condition.name;
}

/**
 * Resolve a party member's DEAD flag for display, mirroring how the lobby panel
 * resolves HP and conditions: the canonical `Character.permanent.status` wins
 * whenever the Character store has loaded, falling back to the denormalized
 * `PartyMemberInfo.isDead` cache only when it hasn't (e.g. an NPC that just
 * joined and has no store entry yet).
 *
 * The cache is refreshed exclusively by `lobby:party-updated`. A Tavian
 * resurrection broadcasts `CharacterUpdated` only (never `PartyUpdated`), so the
 * cached `isDead` stays `true` after a revive — trusting it left a revived PC
 * stuck on the DEAD badge until a full reconnect. Always prefer canonical status.
 *
 * @param permanent The canonical `Character.permanent` (or undefined if not loaded)
 * @param cachedIsDead The denormalized `PartyMemberInfo.isDead` fallback
 */
export function resolveMemberIsDead(
  permanent: { status?: string } | undefined,
  cachedIsDead: boolean | undefined
): boolean {
  if (permanent) return permanent.status === 'dead';
  return cachedIsDead ?? false;
}

/**
 * Effective maximum HP, mirroring the backend `CharacterData::max_hp()`:
 * `permanent.max_hp + session.hp_max_bonus - session.hp_max_reduction`, clamped
 * to >= 0. Buffs like Aid raise the effective max (and current HP with it); Life
 * Drain lowers it. Rendering the raw `permanent.max_hp` instead let current HP
 * exceed the shown max after an Aid buff (e.g. 37/32). Use this everywhere a
 * lobby surface shows a character's max HP so it matches the in-game HP bar.
 */
export function effectiveMaxHp(
  permanent: { max_hp?: number } | undefined,
  session: { hp_max_bonus?: number; hp_max_reduction?: number } | undefined,
  fallback = 10
): number {
  const base = permanent?.max_hp ?? fallback;
  const bonus = session?.hp_max_bonus ?? 0;
  const reduction = session?.hp_max_reduction ?? 0;
  return Math.max(0, base + bonus - reduction);
}

/**
 * Get character status based on HP and conditions
 *
 * Status priority (D&D 5E rules):
 * 1. "Dead" condition → Dead
 * 2. Massive damage (negative HP >= max HP) → Dead
 * 3. HP <= 0 → Unconscious (for PCs making death saves)
 * 4. "Unconscious" condition → Unconscious (from spell/effect)
 * 5. "Sleep"/"Asleep" condition → Sleep
 * 6. "Poisoned" condition → Poisoned
 * 7. "Stunned" condition → Stunned
 * 8. Otherwise → Alive
 *
 * @param hp Current HP (can be negative)
 * @param maxHp Maximum HP
 * @param conditions Array of condition names or WireConditionEntry objects (case-insensitive)
 */
export function getCharacterStatus(
  hp: number,
  maxHp: number,
  conditions: (string | WireConditionEntry)[],
  isDead?: boolean
): CharacterStatus {
  // Backend-authoritative dead flag wins over any HP/condition derivation.
  // HP is clamped to 0, so a dead PC at 0 HP is otherwise indistinguishable
  // from an unconscious (dying) one without this signal.
  if (isDead) {
    return { label: 'DEAD', bgColor: 'bg-gray-800', textColor: 'text-gray-200' };
  }

  const lowerConditions = conditions.map((c) =>
    getConditionName(c as WireConditionEntry).toLowerCase()
  );

  // Explicit dead condition
  if (lowerConditions.includes('dead')) {
    return { label: 'DEAD', bgColor: 'bg-gray-800', textColor: 'text-gray-200' };
  }

  // Massive damage: negative HP >= max HP = instant death
  if (hp < 0 && Math.abs(hp) >= maxHp) {
    return { label: 'DEAD', bgColor: 'bg-gray-800', textColor: 'text-gray-200' };
  }

  // PCs at 0 HP are unconscious (making death saves per D&D 5E rules)
  if (hp <= 0) {
    return { label: 'UNCONSCIOUS', bgColor: 'bg-red-600', textColor: 'text-white' };
  }

  // Unconscious from spell/effect (different color to distinguish from 0 HP)
  if (lowerConditions.includes('unconscious')) {
    return { label: 'UNCONSCIOUS', bgColor: 'bg-purple-600', textColor: 'text-white' };
  }

  // Sleep/asleep conditions
  if (lowerConditions.includes('sleep') || lowerConditions.includes('asleep')) {
    return { label: 'SLEEP', bgColor: 'bg-indigo-600', textColor: 'text-white' };
  }

  // Poisoned condition
  if (lowerConditions.includes('poisoned')) {
    return { label: 'POISONED', bgColor: 'bg-green-700', textColor: 'text-white' };
  }

  // Stunned condition
  if (lowerConditions.includes('stunned')) {
    return { label: 'STUNNED', bgColor: 'bg-yellow-600', textColor: 'text-black' };
  }

  // Default: alive and well
  return { label: 'ALIVE', bgColor: 'bg-green-600', textColor: 'text-white' };
}

/**
 * Get status for monsters/NPCs (different rules than PCs)
 *
 * Key difference from getCharacterStatus:
 * - Monsters at 0 HP are "Dead" (no death saves per D&D 5E rules)
 * - PCs at 0 HP are "Unconscious" (making death saves)
 *
 * @param hp Current HP
 * @param maxHp Maximum HP
 * @param conditions Array of condition names
 * @param isMonster Whether this is a monster/NPC (true) or PC (false)
 */
export function getCharacterStatusWithType(
  hp: number,
  maxHp: number,
  conditions: (string | WireConditionEntry)[],
  isMonster: boolean,
  isDead?: boolean
): CharacterStatus {
  // Backend-authoritative dead flag wins over any HP/condition derivation.
  if (isDead) {
    return { label: 'DEAD', bgColor: 'bg-gray-800', textColor: 'text-gray-200' };
  }

  const lowerConditions = conditions.map((c) =>
    getConditionName(c as WireConditionEntry).toLowerCase()
  );

  // Explicit dead condition
  if (lowerConditions.includes('dead')) {
    return { label: 'DEAD', bgColor: 'bg-gray-800', textColor: 'text-gray-200' };
  }

  // Massive damage: negative HP >= max HP = instant death (all types)
  if (hp < 0 && Math.abs(hp) >= maxHp) {
    return { label: 'DEAD', bgColor: 'bg-gray-800', textColor: 'text-gray-200' };
  }

  // At 0 HP:
  // - Monsters/NPCs: Dead (no death saves per D&D 5E)
  // - PCs: Unconscious (making death saves)
  if (hp <= 0) {
    if (isMonster) {
      return { label: 'DEAD', bgColor: 'bg-gray-800', textColor: 'text-gray-200' };
    }
    return { label: 'UNCONSCIOUS', bgColor: 'bg-red-600', textColor: 'text-white' };
  }

  // Unconscious from spell/effect (different color)
  if (lowerConditions.includes('unconscious')) {
    return { label: 'UNCONSCIOUS', bgColor: 'bg-purple-600', textColor: 'text-white' };
  }

  // Sleep/asleep conditions
  if (lowerConditions.includes('sleep') || lowerConditions.includes('asleep')) {
    return { label: 'SLEEP', bgColor: 'bg-indigo-600', textColor: 'text-white' };
  }

  // Poisoned condition
  if (lowerConditions.includes('poisoned')) {
    return { label: 'POISONED', bgColor: 'bg-green-700', textColor: 'text-white' };
  }

  // Stunned condition
  if (lowerConditions.includes('stunned')) {
    return { label: 'STUNNED', bgColor: 'bg-yellow-600', textColor: 'text-black' };
  }

  return { label: 'ALIVE', bgColor: 'bg-green-600', textColor: 'text-white' };
}
